import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { isPlatform } from '../utilities/platform';
import { startView, endView } from '../stores/Stats';

const GAME_SLUGS = ['tourbillon', 'pingpong', 'memory', 'sudok', 'puzzle', 'reordon', 'melimots', 'quizz'];
const ACTIVITY_STORAGE_KEY = 'route_activity';

interface CurrentActivity {
    area: string;
    route: string;
    activityId: string;
    startTime: number;
}

export const useRouteActivity = (token?: string) => {
    const location = useLocation();
    const currentRef = useRef<CurrentActivity | null>(null);

    const getAreaFromPath = (pathname: string): string | null => {
        if (!pathname) return null;

        // Normalize
        if (pathname === '/games' || pathname.startsWith('/games/')) return 'games';

        const segments = pathname.split('/').filter(Boolean);
        if (segments.length > 0 && GAME_SLUGS.includes(segments[0])) return 'games';

        if (pathname === '/album' || pathname.startsWith('/album/')) return 'album';

        return null;
    };

    const saveCurrent = (act: CurrentActivity | null) => {
        currentRef.current = act;
        try {
            if (act) {
                localStorage.setItem(ACTIVITY_STORAGE_KEY, JSON.stringify(act));
            } else {
                localStorage.removeItem(ACTIVITY_STORAGE_KEY);
            }
        } catch {
            // ignore
        }
    };

    const restoreFromStorage = () => {
        try {
            const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.activityId && parsed.startTime) return parsed as CurrentActivity;
        } catch {
            // ignore
        }
        return null;
    };

    const resolveToken = (): string | null => {
        if (token) return token;
        try {
            return localStorage.getItem('access_token') || null;
        } catch {
            return null;
        }
    };

    const start = async (area: string, route: string) => {
        const t = resolveToken();
        if (!t) {
            console.warn('[route-activity] start skipped (no token)', { area, route });
            return;
        }
        const activityId = (crypto as any).randomUUID ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2);
        const now = Date.now();
        saveCurrent({ area, route, activityId, startTime: now });

        try {
            await startView(t, {
                action: 'view_start',
                details: { activity_id: activityId, route, area, timestamp: new Date(now).toISOString() }
            });
        } catch (e) {
            console.error('Failed to send view_start', e);
        }
    };

    const end = async (useKeepalive = true) => {
        const t = resolveToken();
        const current = currentRef.current || restoreFromStorage();
        if (!current) {
            return;
        }
        if (!t) {
            console.warn('[route-activity] end skipped (no token)', { activityId: current.activityId, route: current.route });
            // Still clear local state to avoid duplicate attempts later
            saveCurrent(null);
            return;
        }
        const now = Date.now();
        const duration = Math.floor((now - current.startTime) / 1000);
        try {
            const res = await endView(t, {
                action: 'view_end',
                details: { activity_id: current.activityId, route: current.route, area: current.area, duration, timestamp: new Date(now).toISOString() }
            }, useKeepalive);
        } catch (e) {
            console.error('Failed to send view_end', e);
        } finally {
            saveCurrent(null);
        }
    };

    // Listen to route changes
    useEffect(() => {
        const run = async () => {
            const area = getAreaFromPath(location.pathname);
            const current = currentRef.current || restoreFromStorage();

            const currentArea = current ? current.area : null;
            const route = location.pathname;
            const t = resolveToken();

            if (currentArea !== area) {
                // left previous area
                if (currentArea) {
                    await end();
                }
                // entered new area
                if (area) {
                    await start(area, route);
                }
            } else if (area && current && current.route !== route) {
                // route changed but still in same area -> update stored route only (avoid extra start/end calls)
                const updated = { ...current, route };
                saveCurrent(updated);
            }
        };
        run();
    }, [location.pathname, token]);

    // Visibility handling (pause on hidden)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                end(true);
            } else {
                // start a new activity on visible, using current location
                const area = getAreaFromPath(location.pathname);
                if (area) start(area, location.pathname);
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [location.pathname, token]);

    // beforeunload
    useEffect(() => {
        const handleBeforeUnload = () => {
            end(true);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [token]);

    // Capacitor pause/resume
    useEffect(() => {
        if (!isPlatform('hybrid')) return;

        const onPause = async () => {
            await end(true);
        };
        const onResume = async () => {
            const area = getAreaFromPath(location.pathname);
            if (area) start(area, location.pathname);
        };

        CapacitorApp.addListener('pause', onPause);
        CapacitorApp.addListener('resume', onResume);

        return () => {
            CapacitorApp.removeAllListeners();
        };
    }, [location.pathname, token]);
};

export default useRouteActivity;
